import {Component, computed, inject} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router} from '@angular/router';
import {filter, map, startWith} from 'rxjs';
import {Message} from 'primeng/message';
import {TaCommentsComponent} from '../ta-comments/ta-comments';
import {TaCommentCapabilities, taCommentCapabilitiesFor} from '../../../util/app.types';
import {TaAppStore} from '../../../state/clcta-store';
import {PermissionService} from '../../../services/permission.service';

@Component({
  selector: 'bread-crumb-bar',
  imports: [Message, TaCommentsComponent],
  templateUrl: './bread-crumb-bar.component.html',
  styleUrl: './bread-crumb-bar.component.scss'
})
export class BreadCrumbBarComponent {
  store = inject(TaAppStore);
  private permissionService = inject(PermissionService);
  private router = inject(Router);

  // Route-level opt-in (`showAccountBlocked` in app.routes.ts) so no page has to manage the
  // banner itself. The bar sits outside the router-outlet, so walk to the active leaf route
  // rather than reading this component's own ActivatedRoute - which is always the root.
  private bannerRoute = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      startWith(null),
      map(() => {
        let route = this.router.routerState.root;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route.snapshot.data['showAccountBlocked'] === true;
      }),
    ),
    {initialValue: false},
  );

  accountBlocked = computed<boolean>(() => this.bannerRoute() && this.permissionService.accountBlocked());
  blockedMessage = this.permissionService.blockedMessage;

  // TA pages opt in with a `taNumberPrefix` and the number is composed here rather than in each
  // page's ngOnInit - the TA loads asynchronously, so a one-shot read would miss it on a refresh.
  screenTitle = computed<string>(() => {
    const {title, taNumberPrefix} = this.store.breadCrumbData();
    const taNumber = this.store.taSubmission()?.taNumber;
    return taNumberPrefix !== undefined && !!taNumber ? taNumberPrefix + taNumber : title;
  });
  iconClass = computed(() => this.store.breadCrumbData().titleIcon);

  // The whole autosave indicator: a save is outstanding on a page that opted in. The queue is
  // app-wide, hence the showSaveStatus gate - see BreadCrumbData.
  showSaving = computed<boolean>(() =>
    !!this.store.breadCrumbData().showSaveStatus && this.store.isSavingData());
  // Both halves have to hold: the route opts in (create/review), and the TA it belongs to has
  // arrived - the bar renders long before the submission does on a refresh.
  showTaComments = computed<boolean>(() =>
    this.store.breadCrumbData().showTaComments === true && !!this.store.taSubmission()?.id);
  taSubmissionId = computed<string | undefined>(() => this.store.taSubmission()?.id ?? undefined);
  // What the chain lets the user do, worked out here rather than in the component - see
  // taCommentCapabilitiesFor.
  taCommentCapabilities = computed<TaCommentCapabilities>(
    () => taCommentCapabilitiesFor(this.store.taSubmission()?.status));
  templateMode = computed<boolean>(() => this.store.breadCrumbData().templateMode === true);
  breadTrail = computed<string>(() => {
    let breadTrail = ''
    this.store.breadCrumbData().breadTrail.forEach((breadCrumb) => {
      breadTrail = breadTrail + ' > ' + breadCrumb
    })
    return breadTrail;
  });
}
